import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Assets from './Assets';
import {
  PAGE_MUI_ICONS,
  flattenMenuPages,
  getAdminPath,
  getBottomNavLabel,
  getBottomNavPages,
  getIconPath,
  isMenuPathActive,
} from '../../Utils/adminNavigation';

export const MOBILE_BOTTOM_NAV_HEIGHT = 56;

export const mobileBottomNavOffset = `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`;

const ACTIVE_COLOR = '#3E4094';
const INACTIVE_COLOR = '#94a3b8';

const NavItem = ({ active, label, onClick, onKeyDown, ariaLabel, title, children }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={onKeyDown}
    title={title}
    aria-label={ariaLabel}
    aria-current={active ? 'page' : undefined}
    sx={{
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 88,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3px',
      py: '6px',
      px: '4px',
      mx: '2px',
      borderRadius: '12px',
      cursor: 'pointer',
      position: 'relative',
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation',
      backgroundColor: active ? 'rgba(62, 64, 148, 0.1)' : 'transparent',
      transition: 'background-color 0.15s ease',
      '&:active': {
        backgroundColor: active ? 'rgba(62, 64, 148, 0.16)' : 'rgba(15, 23, 42, 0.05)',
      },
      '&::before': active
        ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 20,
            height: 3,
            borderRadius: '0 0 3px 3px',
            backgroundColor: ACTIVE_COLOR,
          }
        : undefined,
    }}
  >
    {children}
    <Typography
      component="span"
      sx={{
        fontSize: '11px',
        lineHeight: 1.15,
        fontWeight: active ? 600 : 500,
        color: active ? ACTIVE_COLOR : INACTIVE_COLOR,
        textAlign: 'center',
        width: '100%',
        px: 0.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const MobileBottomNav = ({ onMoreClick, groupedPages, sectionOrder }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { primary: bottomPages, showMore } = useMemo(
    () => getBottomNavPages(groupedPages, sectionOrder),
    [groupedPages, sectionOrder]
  );

  const isPrimaryActive = useMemo(
    () => bottomPages.some((page) => isMenuPathActive(location.pathname, page?.path)),
    [bottomPages, location.pathname]
  );

  const isMoreActive = useMemo(() => {
    if (!showMore) return false;
    if (isPrimaryActive) return false;
    const allPages = flattenMenuPages(groupedPages, sectionOrder);
    return allPages.some((page) => isMenuPathActive(location.pathname, page?.path));
  }, [showMore, isPrimaryActive, groupedPages, sectionOrder, location.pathname]);

  if (bottomPages.length === 0 && !showMore) return null;

  const handleNavClick = (page) => {
    navigate(getAdminPath(page?.path));
  };

  const handleKeyActivate = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        display: { xs: 'flex', sm: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderTop: '1px solid rgba(226, 232, 240, 0.9)',
        boxShadow: '0 -4px 16px rgba(15, 23, 42, 0.06)',
        height: mobileBottomNavOffset,
        paddingBottom: 'env(safe-area-inset-bottom)',
        alignItems: 'stretch',
        justifyContent: 'space-around',
        px: 0.5,
      }}
    >
      {bottomPages.map((page) => {
        const active = isMenuPathActive(location.pathname, page?.path);
        const MuiIcon = PAGE_MUI_ICONS[page?.pageName];
        const label = getBottomNavLabel(page?.pageName);

        return (
          <NavItem
            key={page?.pageName || page?.path}
            active={active}
            label={label}
            title={page?.pageName}
            ariaLabel={page?.pageName}
            onClick={() => handleNavClick(page)}
            onKeyDown={(e) => handleKeyActivate(e, () => handleNavClick(page))}
          >
            {MuiIcon ? (
              <MuiIcon
                sx={{
                  fontSize: active ? 22 : 21,
                  color: active ? ACTIVE_COLOR : INACTIVE_COLOR,
                  transition: 'color 0.15s ease',
                }}
              />
            ) : (
              <Assets
                src={getIconPath(page)}
                height={active ? '22px' : '21px'}
                width={active ? '22px' : '21px'}
                style={{
                  filter: active
                    ? 'brightness(0) saturate(100%) invert(24%) sepia(45%) saturate(1282%) hue-rotate(210deg) brightness(92%) contrast(91%)'
                    : 'brightness(0) saturate(100%) invert(62%) sepia(14%) saturate(497%) hue-rotate(176deg) brightness(91%) contrast(86%)',
                }}
              />
            )}
          </NavItem>
        );
      })}

      {showMore && (
        <NavItem
          active={isMoreActive}
          label="More"
          title="More menu items"
          ariaLabel="More menu items"
          onClick={() => onMoreClick?.()}
          onKeyDown={(e) => handleKeyActivate(e, () => onMoreClick?.())}
        >
          <MoreHorizIcon
            sx={{
              fontSize: isMoreActive ? 22 : 21,
              color: isMoreActive ? ACTIVE_COLOR : INACTIVE_COLOR,
            }}
          />
        </NavItem>
      )}
    </Box>
  );
};

export default MobileBottomNav;
